import { LayoutService } from '@common/services/layout.service';
import { ResponsiveDirectiveHandler } from '../responsive.directive';
import { Subscription } from 'rxjs';

function display(container: HTMLElement, query: string, visible: boolean): HTMLElement[] {

  let collection = container.querySelectorAll(query);

  let results: HTMLElement[] = [];

  for (let i = 0; i < collection.length; i++) {
    if (visible) {
      collection[i].classList.remove('hidden');
    }
    else {
      collection[i].classList.add('hidden');
    }

    results.push(collection[i] as HTMLElement);
  }

  return results;
}

function overflow(element: HTMLElement): boolean {
  
  let parent = element.parentElement;
  while (parent && parent.getBoundingClientRect().width == 0) parent = parent.parentElement;

  if (!parent) return false;

  let eInf = window.getComputedStyle(element);
  let pInf = window.getComputedStyle(parent);

  let unusable: number = [eInf.marginLeft == 'auto' ? '0' : eInf.marginLeft, eInf.marginRight == 'auto' ? '0' : eInf.marginRight, pInf.paddingLeft, pInf.paddingRight, pInf.borderLeftWidth, pInf.borderRightWidth].sum(i => parseFloat(i));
  return Math.round(parent.getBoundingClientRect().width - unusable) < element.clientWidth;
}

class Column {

  private table: HTMLElement;
  private th: HTMLTableCellElement;
  private index: number;
  public priority: number;

  constructor(table: HTMLTableElement, th: HTMLTableHeaderCellElement, index: number) {

    this.table = table;
    this.th = th;
    this.index = index;

    this.priority = parseInt(th.getAttribute('priority'));    
  }
  private get elements(): HTMLElement[] {

    let results: HTMLElement[] = [this.th];

    let tds = this.table.querySelectorAll('tr:not(.size-warning) > td:nth-child(' + (this.index + 1) + ')');
    for (let i = 0; i < tds.length; i++) {
      results.push(tds[i] as HTMLElement);
    }

    return results;
  }

  public set visible(value: boolean) {

    this.elements.forEach(i => {
      if (value) {
        i.classList.remove('hidden');
      }
      else {
        i.classList.add('hidden');
      }
    });
  }

  public get visible(): boolean {
    return !this.th.classList.contains('hidden');
  }
}

export class ResponsiveTable implements ResponsiveDirectiveHandler {

  public static sizeWarningHTMLMessage: (hiddenColumnCount: number) => string = count => '<div><i class="fa fa-exclamation-circle"></i><span>' + count + ' cột thông tin ẩn do màn hình nhỏ</span></div>';

  private table: HTMLTableElement;

  private warning: HTMLTableRowElement = document.createElement('tr');

  private mObserver = new MutationObserver(records => this.style(records));
  private rObserver = new (window as any).ResizeObserver(() => { this.response() });
  private rSubscription: Subscription;


  private lastChecked: { width: number, hidden: number, visible: number };

  constructor(table: HTMLTableElement, layout: LayoutService) {    

    this.table = table;
    
    this.warning.appendChild(document.createElement('td'));
    this.warning.className = 'size-warning';

    this.mObserver.observe(table, { childList: true });
    this.rObserver.observe(this.table);
    this.rSubscription = layout.resize.subscribe(e => {
      if (e.type == 'content' && e.scope.contains(this.table)) {
        this.response();
      }
    });
  }

  public destroy() {
    this.mObserver.disconnect();
    this.mObserver.disconnect();
    this.rSubscription.unsubscribe();
  }

  private warn(count: number) {

    if (this.table.classList.contains('no-size-warning')) return;

    if (!this.warning.parentElement && this.table.childElementCount > 0) {
      if (this.table.childElementCount > 1) {
        this.table.insertBefore(this.warning, this.table.childNodes[1]);
      }
      else {
        this.table.appendChild(this.warning);
      }
    }

    if (this.warning.parentElement) {
      if (count > 0 && this.table.childElementCount > 2) {
        this.warning.firstElementChild.innerHTML = ResponsiveTable.sizeWarningHTMLMessage(count);
        (this.warning.firstElementChild as HTMLTableDataCellElement).colSpan = this.table.getElementsByTagName('th').length - count;
        this.warning.classList.remove('hidden');
        this.table.classList.add('partial');
      }
      else {
        this.warning.classList.add('hidden');
        this.table.classList.remove('partial');
      }
    }
  }

  private response() {

    let ths = this.table.getElementsByTagName('th');

    if (ths.length > 0) {

      if (!overflow(this.table) && this.lastChecked) {
        if (this.lastChecked.width < this.table.clientWidth && this.lastChecked.hidden < 1) return;
        if (this.lastChecked.width >= this.table.clientWidth && this.lastChecked.visible > 0) return;
      }

      let count: number = ths.length;

      let cols: Column[] = [];
      for (let i = 0; i < count; i++) {
        cols.push(new Column(this.table, ths[i], i));
      }

      let invisibles = cols.filter(i => !i.visible);

      while (!overflow(this.table) && invisibles.length > 0) {

        let min: number = invisibles.min(i => i.priority);
        invisibles.filter(i => i.priority == min).forEach(i => {
          i.visible = true;
          display(this.table, '.hidden-until-' + i.priority, i.visible);
          display(this.table, '.hidden-since-' + i.priority, !i.visible);

          invisibles.splice(invisibles.indexOf(i), 1);
        });
      }

      let visibles = cols.filter(i => i.visible);

      while (overflow(this.table) && visibles.length > 0) {

        let max: number = visibles.max(i => i.priority);

        visibles.filter(i => i.priority == max).forEach(i => {
          i.visible = false;
          display(this.table, '.hidden-until-' + i.priority, i.visible);
          display(this.table, '.hidden-since-' + i.priority, !i.visible);

          visibles.splice(visibles.indexOf(i), 1);
        });
      }

      this.lastChecked = {
        width: this.table.clientWidth,
        visible: cols.length,
        hidden: cols.count(i => !i.visible)
      }
      this.lastChecked.visible -= this.lastChecked.hidden;

      this.warn(this.lastChecked.hidden);
    }
  }

  private style(records: MutationRecord[]) {

    let ths = this.table.getElementsByTagName('th')

    if (ths.length > 0) {

      if (!this.table['reponsive-directive-initialized']) {

        for (let i = 0; i < ths.length; i++) {

          if (!ths[i].hasAttribute('priority')) {
            ths[i].setAttribute('priority', (i == 0 ? 1 : ths.length - i + 1).toString());
          }
          else {
            let priority = ths[i].getAttribute('priority');
            if (priority == null || priority.length == 0 || priority.replace(/([^0-9]+)/g, '').length != priority.length) {
              ths[i].setAttribute('priority', (i == 0 ? 1 : ths.length - i + 1).toString());
            }
          }
        }

        let tho = [];
        for (let i = 0; i < ths.length; i++) tho.push(ths[i]);

        tho.sort((a, b) => parseInt(a.getAttribute('priority')) > parseInt(b.getAttribute('priority')) ? 1 : -1).forEach(th => {
          display(this.table, '.hidden-until-' + th.getAttribute('priority'), !th.classList.contains('hidden'));
          display(this.table, '.hidden-since-' + th.getAttribute('priority'), th.classList.contains('hidden'));
        });

        this.table['reponsive-directive-initialized'] = true;
      }

      records.forEach(record => {

        for (let n = 0; n < record.addedNodes.length; n++) {
          let tr: HTMLElement = <HTMLElement>record.addedNodes[n];

          if (!tr.tagName || tr.tagName != 'TR' || tr.classList.contains('size-warning')) return;

          let tds = tr.getElementsByTagName('td');

          for (let i = 0; i < Math.min(tds.length, ths.length); i++) {

            if (ths[i].classList.length > 0) tds[i].classList.add(...ths[i].className.split(' '));
            if (!ths[i].classList.contains('hidden')) {
              for (let o = 0; o < ths.length; o++) {
                display(tds[i], '.hidden-until-' + ths[o].getAttribute('priority'), !ths[o].classList.contains('hidden'));
                display(tds[i], '.hidden-since-' + ths[o].getAttribute('priority'), ths[o].classList.contains('hidden'));
              }
            }
          }
        }
      });
    }
  }
}
